import { useState, useEffect, useCallback } from "react";
import supabase from "../supabase/supabase";
import { listPatients } from "../lib/demographic";
/**
 * useClinicalOverviewStats
 *
 * Deliberately independent of PatientList/TriageCenter. HospitalSummaryCard
 * (used inside Triage Centre) is fed by PatientList's onStatsUpdate
 * callback, which only fires while PatientList is mounted — that's fine
 * for Triage Centre, but the Clinical Dashboard Overview needs numbers
 * that are still correct when nobody is on the Triage page. This hook
 * runs its own query instead of relying on another page's side effect.
 *
 * Item 6.5 follow-up (25/08/2026): this file was not identified during
 * the original Item 6 scoping (only PatientList.jsx/PatientRoster.jsx
 * were named) and broke in staging once Steps 6.2/6.3 revoked
 * anon/authenticated access to `users` — its direct client-side query
 * against that table started returning 401. Migrated to the same
 * governed GET /api/v1/users route the other two components use.
 *
 * Fields returned:
 *   - activePatients   : real, counted via the governed GET /api/v1/users
 *                         route (platform_user, tenant-scoped, role=patient).
 *   - alertTotals       : null — previously summed thresholds.alerts from
 *                         the legacy `users` table; that column has no
 *                         canonical-side (platform_user/patient) equivalent
 *                         yet. Not fabricated as 0. See: Patient Roster
 *                         Risk Badge, on hold pending canonical patient
 *                         linkage validation. CONSUMER NOTE: whatever
 *                         renders this value needs to handle null, not
 *                         assume a number — unverified as of this patch.
 *   - deviceUptimePct   : real, (# devices with status='active') / (total
 *                         devices) * 100, from the `devices` table.
 *                         Unaffected by Item 6 — devices' RLS/grants were
 *                         not touched.
 *   - avgResponseTimeMinutes : null — no response-time tracking exists yet
 *                         anywhere in the schema. Not fabricated.
 *   - populationAnalyticsAvailable : false — no analytics pipeline exists
 *                         yet. Card renders a "Coming soon" state instead
 *                         of invented charts/numbers.
 */
export default function useClinicalOverviewStats() {
  const [stats, setStats] = useState({
    activePatients: 0,
    alertTotals: null,
    deviceUptimePct: null,
    avgResponseTimeMinutes: null,
    populationAnalyticsAvailable: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const platformUsers = await listPatients({ limit: 1000 });
      const activePatients = (platformUsers || []).length;

      // TODO: no canonical clinical/threshold source yet — previously
      // summed thresholds.alerts from the legacy `users` table, which
      // Item 6 (Steps 6.2/6.3) removed anon/authenticated access to.
      // Do not fabricate a numeric default here.
      const alertTotals = null;

      const { data: devices, error: devicesError } = await supabase
        .from("devices")
        .select("status")
        .limit(2000);
      if (devicesError) throw devicesError;
      const totalDevices = (devices || []).length;
      const activeDevices = (devices || []).filter((d) => d.status === "active").length;
      const deviceUptimePct = totalDevices > 0 ? Math.round((activeDevices / totalDevices) * 100) : null;
      setStats({
        activePatients,
        alertTotals,
        deviceUptimePct,
        avgResponseTimeMinutes: null, // TODO: no response-time tracking source yet
        populationAnalyticsAvailable: false, // TODO: no analytics pipeline yet
      });
    } catch (err) {
      console.error(
        "[useClinicalOverviewStats] fetch error:",
        err?.response?.data?.message || err?.message || err,
      );
      setError("Could not load hospital summary.");
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  return { stats, isLoading, error, refresh: fetchStats };
}